import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Typography, Button, Space, Form, Input, DatePicker, Upload, Avatar, Divider, Descriptions, message, Select } from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { setProfile } from '../../store/interfaceSlice';
import { useSocketContext } from '../../sockets/SocketContext';
import RequestsHistory from './RequestsHistory';
import { ensureDataUrl } from '../../utils/helpers';
// import { selectTask } from '../../store/tasksSlice';

const { Title } = Typography;

// История вынесена в RequestsHistory.jsx

const Cabinet = () => {
  // Все хуки должны быть в начале компонента
  const dispatch = useDispatch();
  const { username, profile } = useSelector((s) => s.interface);
  const { updateProfileOnServer, getProfileFromServer } = useSocketContext();
  const [profileForm] = Form.useForm();
  const [avatarPreview, setAvatarPreview] = React.useState(profile?.avatarDataUrl || null);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  useEffect(() => {
    if (!editMode) {
      setAvatarPreview(profile?.avatarDataUrl || null);
    }
  }, [profile?.avatarDataUrl, editMode]);

  useEffect(() => {
    if (editMode && profile) {
      // Добавляем небольшую задержку, чтобы Form элемент успел отрендериться
      const timer = setTimeout(() => {
        try {
          profileForm.setFieldsValue({
            displayName: profile?.displayName || username || '',
            bio: profile?.bio || '',
            location: profile?.location || '',
            gender: profile?.gender || '',
            birthDate: profile?.birthDate ? window.dayjs?.(profile.birthDate) : null,
          });
        } catch (error) {
          console.warn('Form not ready yet:', error);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [editMode, profileForm, profile, username]);

  const handleAvatarChange = ({ fileList }) => {
    if (!fileList.length) {
      setAvatarPreview(null);
      return;
    }
    const file = fileList[fileList.length - 1].originFileObj;
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleProfileSubmit = (values) => {
    // Преобразуем дату в ISO, если есть
    const birthDateIso = values.birthDate && values.birthDate.toDate ? values.birthDate.toDate().toISOString() : (values.birthDate?.toISOString?.() || null);
    const payload = {
      displayName: values.displayName || '',
      bio: values.bio || '',
      location: values.location || '',
      gender: values.gender || '',
      birthDate: birthDateIso,
      avatarDataUrl: avatarPreview || null,
    };
    setSaving(true);
    updateProfileOnServer(payload, (err, saved) => {
      if (err) {
        message.error(err || 'Не удалось сохранить профиль');
        setSaving(false);
        return;
      }
      dispatch(setProfile(saved));
      message.success('Профиль сохранён');
      setEditMode(false);
      setSaving(false);
      // После удачного сохранения – перезапрос профиля, чтобы гарантировать консистентность (если бэкенд что-то нормализует)
      try { getProfileFromServer?.(); } catch {}
    });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setAvatarPreview(profile?.avatarDataUrl || null);
    // Сбрасываем значения формы только если она существует
    if (editMode) {
      try {
        profileForm.resetFields();
      } catch (error) {
        console.warn('Form not ready for reset:', error);
      }
    }
  };

  return (
    <Card>
      <Title level={4} style={{ marginBottom: 16 }}>Личный кабинет{username ? ` — ${username}` : ''}</Title>

      {/* Профиль */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="Профиль"
        extra={
          !editMode ? (
            <Button type="default" onClick={() => setEditMode(true)}>Изменить</Button>
          ) : (
            <Space>
              <Button type="primary" onClick={() => profileForm.submit()} loading={saving}>Сохранить</Button>
              <Button onClick={handleCancelEdit}>Отмена</Button>
            </Space>
          )
        }
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Avatar
              size={96}
              key={avatarPreview || profile?.avatarDataUrl || 'no-avatar'}
              src={ensureDataUrl(avatarPreview || profile?.avatarDataUrl) || undefined}
              icon={!avatarPreview && !profile?.avatarDataUrl ? <UserOutlined /> : undefined}
              style={{ border: '1px solid #f0f0f0' }}
            />
            {editMode && (
              <Upload
                maxCount={1}
                beforeUpload={() => false}
                onChange={handleAvatarChange}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />}>Загрузить аватар</Button>
              </Upload>
            )}
          </div>

          {!editMode ? (
            <div style={{ flex: 1, minWidth: 260 }}>
              <Typography.Title level={5} style={{ marginTop: 0 }}>{profile?.displayName || username || 'Без имени'}</Typography.Title>
              <Descriptions column={1} size="small" colon>
                <Descriptions.Item label="Описание">{profile?.bio || '—'}</Descriptions.Item>
                <Descriptions.Item label="Местоположение">{profile?.location || '—'}</Descriptions.Item>
                <Descriptions.Item label="Пол">{({ 'male': 'Мужской', 'female': 'Женский', 'other': 'Другое' }[profile?.gender || ''] || 'Не указан')}</Descriptions.Item>
                <Descriptions.Item label="Дата рождения">{profile?.birthDate ? new Date(profile.birthDate).toLocaleDateString() : '—'}</Descriptions.Item>
              </Descriptions>
            </div>
          ) : (
            <Form
              form={profileForm}
              layout="vertical"
              style={{ flex: 1, minWidth: 260 }}
              onFinish={handleProfileSubmit}
            >
              <Form.Item label="Имя" name="displayName">
                <Input placeholder="Ваше имя" />
              </Form.Item>
              <Form.Item label="Описание" name="bio">
                <Input.TextArea placeholder="Коротко о себе" autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Form.Item label="Местоположение" name="location" style={{ flex: 1, minWidth: 200 }}>
                  <Input placeholder="Город, страна" />
                </Form.Item>
                <Form.Item label="Пол" name="gender" style={{ width: 180 }}>
                  <Select
                    options={[
                      { value: '', label: 'Не указан' },
                      { value: 'male', label: 'Мужской' },
                      { value: 'female', label: 'Женский' },
                      { value: 'other', label: 'Другое' },
                    ]}
                  />
                </Form.Item>
                <Form.Item label="Дата рождения" name="birthDate" style={{ width: 220 }}>
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </div>
            </Form>
          )}
        </div>
      </Card>
      <Divider style={{ margin: '12px 0' }} />
      <RequestsHistory />
    </Card>
  );
};

export default Cabinet;


